import * as GalleryViewStyled from './GalleryView.styled';
import { GalleryViewProps } from './GalleryView.typed';
import {
  Svg360OutlineIc,
  PlayCircleOutlineIc,
  PlayOutlineIc,
} from '@dsch/dd-icons';
import getConfig from 'next/config';
import { accessibleOnClick } from '../../../../../utils/Accessibility';
import { THUMBNAIL_LOCATIONS, mediaItemToType } from './galleryOpenMapping';

const GalleryView = ({
  title,
  openGallery,
  photoCount,
  media,
  hasCarousel,
  mainGalleryMediaImage,
  mainGalleryMedia,
  galleryMedia,
  THUMBNAIL_360,
  setIsYoutubeReviewOpen,
  isDisabled = false,
  onMediaClick,
}: GalleryViewProps) => {
  const {
    publicRuntimeConfig: { CDN_STATIC_ASSETS },
  } = getConfig();
  const isClickable = !isDisabled && media && media.length > 0;
  const hasReviewVideo = Boolean(
    media && media.find((mediaItem) => mediaItem.hasReview),
  );

  return (
    <GalleryViewStyled.Wrapper
      isDisabled={isDisabled}
      isClickable={isClickable}
      hasWhiteBackground={media && media.length <= 1}
    >
      <GalleryViewStyled.MainWrapper
        hasCarousel={hasCarousel}
        {...accessibleOnClick(() => {
          if (isClickable) {
            openGallery({
              media_type: mediaItemToType(mainGalleryMedia),
              media_location: 'main image',
            });
          }
          onMediaClick(mainGalleryMedia.id);
        }, 'button')}
      >
        <GalleryViewStyled.ContainerImage simpleImage={!hasCarousel}>
          {mainGalleryMediaImage && (
            <source type="image/webp" srcSet={mainGalleryMediaImage} />
          )}
          <GalleryViewStyled.MainImage
            src={mainGalleryMediaImage}
            alt={hasCarousel ? `${title} - Image 1` : title}
            simpleImage={!hasCarousel}
            width={568}
            height={426}
          />
        </GalleryViewStyled.ContainerImage>

        {setIsYoutubeReviewOpen && hasReviewVideo && (
          <GalleryViewStyled.WatchReview
            {...accessibleOnClick((event) => {
              // Stop the click bubbling to MainWrapper's openGallery, which
              // would fire a second gallery_open ('standard image'). Watch
              // Review opens the lightbox on the review slide itself.
              event?.stopPropagation();
              if (isClickable && setIsYoutubeReviewOpen) {
                setIsYoutubeReviewOpen(true);
                openGallery({
                  media_type: 'watch review',
                  media_location: 'main image',
                });
              }
            }, 'button')}
            data-tracking="watch-review"
          >
            <PlayOutlineIc width="16" height="16" />
            <span>Watch Review</span>
          </GalleryViewStyled.WatchReview>
        )}

        {mainGalleryMedia && mainGalleryMedia.isVideo && (
          <GalleryViewStyled.WatchVideo
            {...accessibleOnClick(() => {
              if (isClickable) {
                openGallery({
                  media_type: 'video',
                  media_location: 'main image',
                });
              }
            }, 'button')}
            data-tracking="watch-review"
          >
            <GalleryViewStyled.YouTubeIcon
              src={`${CDN_STATIC_ASSETS}/images/illustrations/youtube-icon.svg`}
            />
          </GalleryViewStyled.WatchVideo>
        )}
        {mainGalleryMedia && mainGalleryMedia.is360 && (
          <GalleryViewStyled.OverlayContent opacity={false}>
            <Svg360OutlineIc width="40" height="40" />
          </GalleryViewStyled.OverlayContent>
        )}
      </GalleryViewStyled.MainWrapper>
      {hasCarousel && (
        <GalleryViewStyled.ListWrapper>
          {galleryMedia &&
            galleryMedia.length > 0 &&
            galleryMedia.map((item, index) => {
              const altImage = `${title} - Image ${index + 2}`;

              if (index < 3) {
                const thumbnailLocation = THUMBNAIL_LOCATIONS[index];
                if (index === 2 && item.isPhoto && media.length > 4) {
                  return (
                    <GalleryViewStyled.ListImage
                      key={index}
                      {...accessibleOnClick(() => {
                        if (isClickable) {
                          openGallery({
                            media_type: mediaItemToType(item),
                            media_location: thumbnailLocation,
                          });
                        }
                        onMediaClick(mainGalleryMedia.id);
                      }, 'button')}
                      data-tracking="gallery-thumbnail-click"
                    >
                      {item.isPhoto && photoCount && (
                        <>
                          <GalleryViewStyled.OverlayContent opacity={true}>
                            <GalleryViewStyled.ImageCounter>
                              {`+${photoCount}`}
                            </GalleryViewStyled.ImageCounter>
                          </GalleryViewStyled.OverlayContent>
                          <GalleryViewStyled.OverlayImage
                            src={item.src}
                            alt={altImage}
                          />
                        </>
                      )}
                    </GalleryViewStyled.ListImage>
                  );
                }

                return (
                  <GalleryViewStyled.ListImage
                    {...accessibleOnClick(() => {
                      if (isClickable) {
                        openGallery({
                          media_type: mediaItemToType(item),
                          media_location: thumbnailLocation,
                        });
                      }
                      onMediaClick(item.id);
                    }, 'button')}
                    key={index}
                    data-tracking="gallery-thumbnail-click"
                  >
                    {item.isVideo && (
                      <>
                        <GalleryViewStyled.OverlayContent opacity={true}>
                          <PlayCircleOutlineIc width="40" height="40" />
                        </GalleryViewStyled.OverlayContent>
                        <GalleryViewStyled.OverlayImage
                          src={item.src}
                          alt={altImage}
                        />
                      </>
                    )}
                    {item.is360 && (
                      <>
                        <GalleryViewStyled.OverlayContent opacity={true}>
                          <Svg360OutlineIc width="40" height="40" />
                        </GalleryViewStyled.OverlayContent>
                        <GalleryViewStyled.OverlayImage
                          src={THUMBNAIL_360}
                          alt={altImage}
                        />
                      </>
                    )}
                    {item.isPhoto && (
                      <>
                        <GalleryViewStyled.OverlayImage
                          src={item.src}
                          alt={altImage}
                        />
                      </>
                    )}
                  </GalleryViewStyled.ListImage>
                );
              }
            })}
        </GalleryViewStyled.ListWrapper>
      )}
    </GalleryViewStyled.Wrapper>
  );
};

export { GalleryView };
